// Types für den Onboarding-Interview-Prozess

export type OnboardingStepId = 
  | 'welcome'
  | 'companyName'
  | 'address'
  | 'commercialRegister'
  | 'bankDetails'
  | 'taxNumber'
  | 'logo'
  | 'completion';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  isRequired: boolean;
  isConditional?: boolean; // z.B. Handelsregister nur für Kapitalgesellschaften
  conditionField?: string; // Feld, von dem die Bedingung abhängt
}

export interface OnboardingProgress {
  currentStep: OnboardingStepId;
  completedSteps: OnboardingStepId[];
  startedAt: string;
  lastUpdatedAt: string;
  isCompleted: boolean;
  skippedSteps: OnboardingStepId[];
}

// Rechtsformen die ein Handelsregister benötigen
export const CORPORATION_INDICATORS = [
  'GmbH',
  'gmbh',
  'UG',
  'ug',
  'AG',
  'ag',
  'KGaA',
  'kgaa',
  'SE',
  'se',
  'haftungsbeschränkt',
  'Aktiengesellschaft',
  'Gesellschaft mit beschränkter Haftung',
  'e.K.',
  'e.K',
  'eK',
  'eingetragener Kaufmann',
  'eingetragene Kauffrau',
  'OHG',
  'ohg',
  'KG',
  'kg',
  'GmbH & Co. KG',
  'GmbH & Co KG',
  'UG & Co. KG',
];

// Prüft ob der Firmenname auf eine Kapitalgesellschaft hindeutet
export const isLikelyCorporation = (companyName: string): boolean => {
  if (!companyName) return false;
  const normalizedName = companyName.trim();
  return CORPORATION_INDICATORS.some(indicator => 
    normalizedName.includes(indicator)
  );
};

// Definiert die Reihenfolge und Konfiguration der Onboarding-Schritte
export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'onboarding:steps.welcome.title',
    description: 'onboarding:steps.welcome.description',
    isRequired: true,
  },
  {
    id: 'companyName',
    title: 'onboarding:steps.companyName.title',
    description: 'onboarding:steps.companyName.description',
    isRequired: true,
  },
  {
    id: 'address',
    title: 'onboarding:steps.address.title',
    description: 'onboarding:steps.address.description',
    isRequired: true,
  },
  {
    id: 'commercialRegister',
    title: 'onboarding:steps.commercialRegister.title',
    description: 'onboarding:steps.commercialRegister.description',
    isRequired: false,
    isConditional: true,
    conditionField: 'name', // Abhängig vom Firmennamen
  },
  {
    id: 'bankDetails',
    title: 'onboarding:steps.bankDetails.title',
    description: 'onboarding:steps.bankDetails.description',
    isRequired: false,
  },
  {
    id: 'taxNumber',
    title: 'onboarding:steps.taxNumber.title',
    description: 'onboarding:steps.taxNumber.description',
    isRequired: false,
  },
  {
    id: 'logo',
    title: 'onboarding:steps.logo.title',
    description: 'onboarding:steps.logo.description',
    isRequired: false,
  },
  {
    id: 'completion',
    title: 'onboarding:steps.completion.title',
    description: 'onboarding:steps.completion.description',
    isRequired: true,
  },
];

// Grenzwerte für die Steuerpflicht (Stand 2024/2025)
export const TAX_THRESHOLDS = {
  kleinunternehmerGrenzeVorjahr: 22000, // € im Vorjahr
  kleinunternehmerGrenzeAktuellesJahr: 50000, // € erwarteter Umsatz
  umsatzsteuerPflichtGrenze: 22000, // Ab diesem Umsatz USt-pflichtig
};

// App-Tour Highlights
export interface TourStep {
  target: string; // CSS-Selektor
  title: string;
  content: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

export const APP_TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="new-menu"]',
    title: 'onboarding:tour.newMenu.title',
    content: 'onboarding:tour.newMenu.content',
    placement: 'bottom',
  },
  {
    target: '[data-tour="documents-menu"]',
    title: 'onboarding:tour.documentsMenu.title',
    content: 'onboarding:tour.documentsMenu.content',
    placement: 'bottom',
  },
  {
    target: '[data-tour="management-menu"]',
    title: 'onboarding:tour.managementMenu.title',
    content: 'onboarding:tour.managementMenu.content',
    placement: 'bottom',
  },
  {
    target: '[data-tour="account-menu"]',
    title: 'onboarding:tour.accountMenu.title',
    content: 'onboarding:tour.accountMenu.content',
    placement: 'bottom',
  },
];

